import { Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getTicketTotalActualMinutes } from '@/utils/ticketTimerManager';
// TIM-A5: collapse the per-display 30s polling kernel onto the shared
// minute-tick singleton. Pre-A5 every <TicketTimerDisplay> mounted with
// `timerStatus === 'running'` owned its own
// `setInterval(fetchTotal, 30000)`; on a 10-running-ticket board that
// was 10 wall-clock-aligned 30s timer kernels each issuing its own
// `getTicketTotalActualMinutes` round trip.
//
// Phase E: when the parent (`TicketCard`) supplies `liveElapsedMinutes`
// from the canonical derived-time record, this component renders that
// value directly and skips the closed-sessions-only DB read entirely.
// Pre-Phase-E `getTicketTotalActualMinutes` returned 0 for any ticket
// whose active session had never been stopped — so a card mid-run
// showed "0h 0m" here while the live timer chip above ticked at
// 10+ hours. With the canonical record fed in, both surfaces share the
// same elapsed-minutes truth.
//
// Cadence tradeoff for the standalone (no-prop) path (60s vs the
// pre-A5 30s): the displayed value is the SUM of CLOSED
// `ticket_timer_sessions` rows (the running session has `stopped_at
// IS NULL` and is excluded — so the label does NOT tick continuously
// while a timer runs). Hidden-tab pause/catch-up (TIM-A3) inherited
// automatically; the V-E-2 R153-MED-1 explicit document.hidden guard
// inside fetchTotal is preserved as defense-in-depth.
import { useSharedMinuteTimer } from '@/hooks/useSharedTimer';

interface TicketTimerDisplayProps {
  ticketId: string;
  timerStatus?: string;
  /**
   * Phase E: optional live elapsed minutes from the canonical
   * `getDerivedTicketTime` record. When supplied, the component
   * renders this value instead of falling back to the closed-
   * sessions-only fetch (which returns 0 for an active session
   * mid-run, causing the "0h 0m vs live chip" disagreement). The
   * prop is optional so non-Dayboard callers (if any) still get the
   * pre-Phase-E behavior; on the Dayboard, `TicketCard` always
   * passes it.
   */
  liveElapsedMinutes?: number;
}

export function TicketTimerDisplay({
  ticketId,
  timerStatus = 'stopped',
  liveElapsedMinutes,
}: TicketTimerDisplayProps) {
  const [fallbackMinutes, setFallbackMinutes] = useState(0);
  const [isLoading, setIsLoading] = useState(liveElapsedMinutes === undefined);
  const minuteTick = useSharedMinuteTimer();
  // Refetch cadence — tied to the shared minute tick ONLY while running,
  // pinned to a constant otherwise. The conditional value is what
  // gates the effect's dep array.
  const refreshKey = timerStatus === 'running' ? minuteTick.getTime() : 0;
  // Phase E: when the parent supplies the canonical value, the
  // fallback DB read is unnecessary — the live chip and this label
  // share the same truth. Skip the fetch entirely in that case.
  const useFallbackFetch = liveElapsedMinutes === undefined;

  useEffect(() => {
    if (!useFallbackFetch) {
      // Live value supplied by parent — no fetch needed.
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchTotal = async () => {
      // V-E-2 R153-MED-1: skip when the tab is hidden. Preserved post-
      // A5 as defense-in-depth alongside TIM-A3's kernel-level
      // visibility gating.
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const mins = await getTicketTotalActualMinutes(ticketId);
        if (!cancelled) setFallbackMinutes(mins);
      } catch (error) {
        console.error('Error fetching total minutes:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTotal();
    return () => {
      cancelled = true;
    };
  }, [ticketId, timerStatus, refreshKey, useFallbackFetch]);

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>...</span>
      </div>
    );
  }

  // Prefer the canonical derived-time minutes when supplied. Otherwise
  // fall back to the closed-sessions-only sum (pre-Phase-E behavior).
  const displayMinutes = useFallbackFetch ? fallbackMinutes : (liveElapsedMinutes ?? 0);

  return (
    // data-timer-status: the timer state this card is rendering, exposed for the multi-user realtime proof
    // (tests/qa/journeys/lifecycle.spec.ts) — an observer has no timer controls, so this is how it reads the state.
    <div className="flex items-center gap-1 text-sm" data-testid="ticket-timer-display" data-timer-status={timerStatus}>
      <Clock className="h-4 w-4" />
      <span className={timerStatus === 'running' ? 'font-semibold text-primary' : ''}>
        {formatTime(displayMinutes)}
      </span>
    </div>
  );
}
